import {Injectable} from '@angular/core';
import {addDaysToDate, salesChannel} from '../../core/utils/utils';
import {PersistentStorage} from '../../core/services/global/storage/persistent-storage.service';
import {SessionStorage} from '../../core/services/global/storage/session-storage.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {SalesChannel} from '../../core/consts/sales-channel.enum';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {QueryParamName} from '../../core/enums/query-param-name.enum';
import {RegExpKey, RegExpService} from './reg-exp.service';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  invalidPartnerName: string;
  private partnerData: PartnerData;
  private readonly PARTNER_DATA_KEY = 'partner';
  private expirationDays: number;

  // new portal data
  private portalPartnerData: PortalPartnerData;
  private readonly PORTAL_PARTNER_DATA_KEY = 'PORTAL_PARTNER';
  private portalPartnerDataExpirationDays: number;

  get isPartnerDataExpired(): boolean {
    const expirationDate = addDaysToDate(new Date(this.partnerData.datetime), this.expirationDays);
    return expirationDate < new Date();
  }

  get isPortalPartnerDataExpired(): boolean {
    const expirationDate = addDaysToDate(new Date(this.portalPartnerData.datetime), this.portalPartnerDataExpirationDays);
    return expirationDate < new Date();
  }

  // localStorage currently not supported in Yanosik
  get localStorageSupported(): boolean {
    return salesChannel() !== SalesChannel.YANOSIK;
  }

  get skyCashExitActionUrl(): string {
    return `skycash://mars.skycash.com/tags/show?id=insurances:webview:tile:${new Date().getTime()}&exitAction=true`;
  }

  get partnerDefined(): boolean {
    const partnerData = this.getPartnerDataFromLS();
    if (isNotNullOrUndefined(partnerData) && isNotNullOrUndefined(partnerData.partnerName)) {
      return true;
    }

    const portalPartnerData = this.getPortalPartnerDataFromLS();
    if (isNotNullOrUndefined(portalPartnerData) && isNotNullOrUndefined(portalPartnerData.partnerId)) {
      return true;
    }

    return false;
  }

  constructor(private persistentStorage: PersistentStorage,
              private sessionStorage: SessionStorage,
              private urlParamsService: UrlParamsService,
              private regExpService: RegExpService) {
  }

  init(expirationDays: number, portalPartnerDataExpirationDays: number): void {
    this.expirationDays = expirationDays;
    this.portalPartnerDataExpirationDays = portalPartnerDataExpirationDays;

    if (this.localStorageSupported) {
      this.handlePartner(this.getPartnerNameFromQueryParams());

      const { partnerId, userId } = this.getPortalPartnerDataFromQueryParams();
      this.handlePortalPartnerData(partnerId, userId);
    }
  }

  getPartnerDataFromLS(): PartnerData {
    return this.localStorageSupported ? JSON.parse(this.persistentStorage.get(this.PARTNER_DATA_KEY)) : null;
  }

  getPortalPartnerDataFromLS(): PortalPartnerData {
    return this.localStorageSupported ? JSON.parse(this.persistentStorage.get(this.PORTAL_PARTNER_DATA_KEY)) : null;
  }

  private getPartnerNameFromQueryParams(): string {
    return this.urlParamsService.getQueryParam(QueryParamName.PARTNER);
  }

  private getPortalPartnerDataFromQueryParams(): any {
    return {
      partnerId: this.urlParamsService.getQueryParam(QueryParamName.PARTNER_ID),
      userId: this.urlParamsService.getQueryParam(QueryParamName.USER_ID)
    }
  }

  private handlePartner(partner: string): void {
    let partnerName;
    if (isNotNullOrUndefined(partner)) { // partner name in URL
      partnerName = partner;
      if (this.isPartnerNameValid(partner)) {
        this.sessionStorage.put(QueryParamName.PARTNER, partner);
        this.savePartnerToLS(partner);
      }
    } else {
      this.partnerData = this.getPartnerDataFromLS();
      if (isNotNullOrUndefined(this.partnerData)) {
        partnerName = this.partnerData.partnerName;
        if (this.isPartnerDataExpired || !this.isPartnerNameValid(this.partnerData.partnerName)) {
          this.persistentStorage.remove(this.PARTNER_DATA_KEY);
        }
      }
    }
    this.handleInvalidPartnerName(partnerName);
  }

  private handlePortalPartnerData(partnerId: string, userId: string): void {
    let partnerName;
    if (isNotNullOrUndefined(partnerId) || isNotNullOrUndefined(userId)) {
      partnerName = partnerId;
      if (this.isPartnerNameValid(partnerId)) {
        this.sessionStorage.put(QueryParamName.PARTNER_ID, partnerId);
      } else {
        partnerId = null;
      }
      this.sessionStorage.put(QueryParamName.USER_ID, userId);
      this.savePortalPartnerDataToLS(partnerId, userId);
    } else {
      this.portalPartnerData = this.getPortalPartnerDataFromLS();
      if (isNotNullOrUndefined(this.portalPartnerData)) {
        partnerName = this.portalPartnerData.partnerId;
        if (this.isPortalPartnerDataExpired || !this.isPartnerNameValid(this.portalPartnerData.partnerId)) {
          this.persistentStorage.remove(this.PORTAL_PARTNER_DATA_KEY);
        }
      }
    }
    this.handleInvalidPartnerName(partnerName);
  }

  private savePartnerToLS(partner: string): void {
    const partnerData: PartnerData = {
      partnerName: partner,
      datetime: new Date()
    };
    this.persistentStorage.put(this.PARTNER_DATA_KEY, JSON.stringify(partnerData));
    localStorage.setItem(this.PARTNER_DATA_KEY, JSON.stringify(partnerData));
  }

  private savePortalPartnerDataToLS(partnerId: string, userId: string): void {
    const partnerData: PortalPartnerData = {
      partnerId: partnerId,
      userId: userId,
      datetime: new Date()
    };
    this.persistentStorage.put(this.PORTAL_PARTNER_DATA_KEY, JSON.stringify(partnerData));
    localStorage.setItem(this.PORTAL_PARTNER_DATA_KEY, JSON.stringify(partnerData));
  }

  private isPartnerNameValid(partner: string): boolean {
    const partnerNameRegExp = new RegExp(this.regExpService.getRegExp(RegExpKey.PARTNER_NAME), 'g');
    return partnerNameRegExp.test(partner);
  }

  private handleInvalidPartnerName(partnerName: string): void {
    if (isNotNullOrUndefined(partnerName) && partnerName.length > 0 && !this.isPartnerNameValid(partnerName)) {
      this.invalidPartnerName = partnerName;
    }
  }

}

export interface PartnerData {
  partnerName: string;
  datetime: Date;
}

export interface PortalPartnerData {
  partnerId: string;
  userId: string;
  datetime: Date;
}

